import type { PasswordLink } from '@smarton-pbx/shared';
import { AlertCircle, Check, CheckCircle2, Copy, type LucideIcon, X } from 'lucide-react';
import { type CSSProperties, type MouseEvent, type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { locale, t } from '../i18n';

export function Field({
  label,
  hint,
  error,
  group,
  children,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  /** For a field made of several controls (e.g. chips): a group, not a <label>, so clicking the text presses nothing. */
  group?: boolean;
  children: ReactNode;
}) {
  const help = error ? <span className="field-error">{error}</span> : hint && <span className="field-hint">{hint}</span>;
  if (group) {
    return (
      <div className="field" role="group" aria-label={label}>
        <span>{label}</span>
        {children}
        {help}
      </div>
    );
  }
  return (
    <label className="field">
      {label}
      {children}
      {help}
    </label>
  );
}

export function Notice({ kind, children }: { kind?: 'error' | 'success'; children: ReactNode }) {
  const Icon = kind === 'error' ? AlertCircle : kind === 'success' ? CheckCircle2 : null;
  return (
    <div className={`notice ${kind ?? ''}`} role={kind === 'error' ? 'alert' : 'status'}>
      {Icon && <Icon size={16} aria-hidden />}
      <div>{children}</div>
    </div>
  );
}

/** A modal on <dialog>, so focus and Escape behave natively. */
export function Dialog({
  open,
  onClose,
  title,
  description,
  wide,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  /** For long forms. */
  wide?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={wide ? 'wide' : undefined}
      onClose={onClose}
      {...(title && { 'aria-labelledby': titleId })}
      {...(description && { 'aria-describedby': descriptionId })}
    >
      {open && (
        <>
          {title && (
            <div className="dialog-header">
              <div>
                <h2 id={titleId}>{title}</h2>
                {description && <p id={descriptionId}>{description}</p>}
              </div>
              <button type="button" className="ghost icon" onClick={onClose} aria-label={t('Close')}>
                <X size={18} aria-hidden />
              </button>
            </div>
          )}
          {children}
        </>
      )}
    </dialog>
  );
}

/** A one-time link for the person (`to`, their address): emailed when email is set up, and here to copy. */
export function CopyLink({ url, expiresAt, email, to }: PasswordLink & { to: string }) {
  const [copied, setCopied] = useState(false);
  const time = new Date(expiresAt).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
  return (
    <div className="stack" style={{ gap: '0.5rem' }}>
      {email === 'sent' && <Notice kind="success">{t('Emailed to {email}.', { email: to })}</Notice>}
      {email === 'failed' && <Notice kind="error">{t('Couldn’t email it to {email}. Send it yourself.', { email: to })}</Notice>}
      <div className="copy-link">
        <input readOnly value={url} onFocus={(e) => e.target.select()} aria-label={t('Link')} data-testid="password-link" />
        <button
          type="button"
          className="secondary"
          onClick={() => {
            void navigator.clipboard.writeText(url).then(() => setCopied(true));
          }}
        >
          {copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
          {copied ? t('Copied') : t('Copy')}
        </button>
      </div>
      <p className="subtle">
        {email === 'sent' ? t('Works once, until {time}.', { time }) : t('Works once, until {time}. Send it to the person yourself.', { time })}
      </p>
    </div>
  );
}

export function PageHeader({ title, description, children }: { title: string; description?: string; children?: ReactNode }) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {children && <div className="row">{children}</div>}
    </div>
  );
}

export function Loading() {
  return (
    <div className="loading" role="status" aria-label={t('Loading')}>
      <div className="spinner" />
    </div>
  );
}

export function EmptyState({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-icon">
        <Icon size={22} aria-hidden />
      </div>
      <h3>{title}</h3>
      {children}
    </div>
  );
}

/** A row's own controls, and its ⋯ menu (drawn elsewhere on the page, but its clicks reach the row). */
const ROW_CONTROLS = 'a, button, input, select, textarea, label, audio, [role="menu"]';

/**
 * Props for a table row that opens its item (to edit it) on a click anywhere
 * in it: not on the row's own buttons, links and menu, and not when the click
 * ends selecting text (to copy an email, say). The ⋯ menu stays, for the keyboard.
 */
export function opensOnClick(open: () => void): { className: string; onClick: (event: MouseEvent) => void } {
  return {
    className: 'clickable',
    onClick: (event) => {
      if ((event.target as Element).closest(ROW_CONTROLS) || window.getSelection()?.toString()) return;
      open();
    },
  };
}

/** "Martynas (test)" is MA, not M(: brackets and punctuation aren't initials. */
export function initials(name: string): string {
  const parts = name
    .replace(/\([^)]*\)/g, ' ')
    .split(/\s+/)
    .map((part) => part.replace(/^[^\p{L}\p{N}]+/u, ''))
    .filter(Boolean);
  const letters = parts.length > 1 ? [parts[0]![0], parts[parts.length - 1]![0]] : [parts[0]?.[0], parts[0]?.[1]];
  return letters.filter(Boolean).join('').toUpperCase() || '?';
}

/** A person's photo (its address), else their initials; the initials too if the photo can't load. */
export function Avatar({ name, photo, large, square }: { name: string; photo?: string | null; large?: boolean; square?: boolean }) {
  const [failed, setFailed] = useState<string | null>(null);
  const shown = photo && photo !== failed ? photo : null;
  return (
    <span className={`avatar ${large ? 'large' : ''} ${square ? 'square' : ''}`} aria-hidden>
      {shown ? <img src={shown} alt="" onError={() => setFailed(shown)} /> : initials(name)}
    </span>
  );
}

export function Pill({ kind, dot, children }: { kind?: 'success' | 'warning' | 'danger' | 'brand'; dot?: boolean; children: ReactNode }) {
  return (
    <span className={`pill ${kind ? `is-${kind}` : ''}`}>
      {dot && <span className="dot" />}
      {children}
    </span>
  );
}

const MENU_GAP = 6;
const VIEWPORT_EDGE = 8;

/**
 * A small dropdown menu that closes on outside click and Escape. It floats over the page (fixed, in a
 * portal) so a table's scroll box can't clip it, and opens the other way when its side has no room.
 */
export function Menu({
  trigger,
  children,
  up,
  label,
}: {
  trigger: (props: { onClick: () => void; 'aria-expanded': boolean; 'aria-haspopup': 'menu' }) => ReactNode;
  children: (close: () => void) => ReactNode;
  up?: boolean;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<CSSProperties>();
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const position = () => {
      const anchor = ref.current?.getBoundingClientRect();
      const menu = menuRef.current;
      if (!anchor || !menu) return;
      const viewport = document.documentElement;
      const height = menu.scrollHeight + 2;
      const width = menu.offsetWidth;
      const below = viewport.clientHeight - anchor.bottom - MENU_GAP - VIEWPORT_EDGE;
      const above = anchor.top - MENU_GAP - VIEWPORT_EDGE;
      const openUp = up ? height <= above || above >= below : height > below && above > below;
      const room = openUp ? above : below;
      const left = up ? anchor.left : anchor.right - width;
      setPlace({
        top: openUp ? anchor.top - MENU_GAP - Math.min(height, room) : anchor.bottom + MENU_GAP,
        left: Math.max(VIEWPORT_EDGE, Math.min(left, viewport.clientWidth - width - VIEWPORT_EDGE)),
        maxHeight: room,
      });
    };
    position();
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
      setPlace(undefined);
    };
  }, [open, up]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!ref.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="menu-anchor" ref={ref} style={up ? { display: 'block' } : undefined}>
      {trigger({ onClick: () => setOpen((o) => !o), 'aria-expanded': open, 'aria-haspopup': 'menu' })}
      {open &&
        createPortal(
          // Measured hidden on the first pass, then placed before the browser paints.
          <div className="menu" role="menu" aria-label={label} ref={menuRef} style={place ?? { top: 0, left: 0, visibility: 'hidden' }}>
            {children(() => setOpen(false))}
          </div>,
          // A modal <dialog> sits in the top layer, so a menu inside one stays inside it.
          ref.current?.closest('dialog') ?? document.body,
        )}
    </div>
  );
}
